/**
 * Handles a scan when no screen has claimed it (see ScanContext).
 *
 * Unit (individual item):
 *   1. on an ACTIVE booking                        -> return prompt
 *   2. allocated to one OPEN booking due by today  -> open it in pickup mode
 *   3. otherwise, OPEN bookings due by today with an identical line
 *      (same product type + size)                  -> picker; choosing one opens
 *                                                     pickup mode, which offers
 *                                                     to swap the scanned unit in
 *   4. nothing                                     -> "no booking found" dialog
 * Product (pool / unlimited):
 *   open bookings due by today and active bookings containing it -> picker
 *   (open directly when there is exactly one).
 *
 * "Due by today" = start date up to the end of today in the merchant's
 * timezone; that includes overdue bookings not yet picked up.
 * See docs/BARCODE_SCANNING_SPEC.md, "Scan Resolution".
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import ScanModal from './ScanModal';
import ReturnScanModal, { type ReturnScanStart } from './ReturnScanModal';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useToast } from '../../contexts/ToastContext';
import { useBarcodeScanning, useScanHandler } from '../../contexts/ScanContext';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { utcIsoToZonedYmd, ymdInZoneToUtcEndIso } from '../../lib/timezone';
import {
  resolveBarcode,
  findBookingsHoldingUnit,
  findPickupCandidatesForUnit,
  findBookingsForProduct,
  unitLabel,
  type ScanBooking,
  type ScannedUnit,
} from '../../services/barcode.service';
import type { PickupScan } from './PickupChecklist';

/** Where the pickup checklist picks the scan up (BookingEdit reads location.state.scan). */
export interface PickupScanState {
  scan: PickupScan;
}

type ScanTarget = { unit?: ScannedUnit; productTypeId?: string };

interface PickerState extends ScanTarget {
  itemLabel: string;
  bookings: ScanBooking[];
}

export function endOfTodayIso(timezone: string): string {
  return ymdInZoneToUtcEndIso(utcIsoToZonedYmd(new Date().toISOString(), timezone), timezone);
}

export default function GlobalScanHandler() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { merchantId, accessControl } = useAuth();
  const { merchantLocale } = useMerchant();
  const { showToast } = useToast();
  const { feedback } = useBarcodeScanning();
  const { formatDateTime } = useDateFormatter();

  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [notFound, setNotFound] = useState<{ code: string; message: string } | null>(null);
  const [returnStart, setReturnStart] = useState<ReturnScanStart | null>(null);

  const openPickup = (bookingId: string, scan: ScanTarget) => {
    const state: PickupScanState = { scan: { ...scan, at: Date.now() } };
    navigate(`/bookings/${bookingId}/edit`, { state });
  };

  const openBooking = (booking: ScanBooking, scan: ScanTarget) => {
    setPicker(null);
    if (booking.booking_state === 'active') {
      setReturnStart({ bookingId: booking.id, unitId: scan.unit?.id, productTypeId: scan.productTypeId });
    } else {
      openPickup(booking.id, scan);
    }
  };

  const handleScan = async (code: string) => {
    if (busy) return;
    setBusy(true);
    setPicker(null);
    setNotFound(null);
    try {
      const { data: res, error } = await resolveBarcode(code, merchantId);
      if (error || !res) {
        feedback('error');
        showToast(t('scan.lookupFailed', { error: error || '' }), 'error');
        return;
      }
      if (res.kind === 'unknown') {
        feedback('error');
        showToast(t('scan.unknownBarcode', { code }), 'error');
        return;
      }

      const endOfToday = endOfTodayIso(merchantLocale.timezone);

      if (res.kind === 'unit') {
        const unit = res.unit;
        const label = `${unit.product_name}${unit.size ? ` (${unit.size})` : ''} · ${unitLabel(unit)}`;
        const { data: holding } = await findBookingsHoldingUnit(accessControl, unit.id);

        const active = holding.find((b) => b.booking_state === 'active');
        if (active) {
          feedback('ok');
          setReturnStart({ bookingId: active.id, unitId: unit.id });
          return;
        }

        const endOfTodayMs = new Date(endOfToday).getTime();
        const allocatedToday = holding.filter(
          (b) => b.booking_state === 'open' && new Date(b.start_date).getTime() <= endOfTodayMs
        );
        if (allocatedToday.length === 1) {
          feedback('ok');
          openPickup(allocatedToday[0].id, { unit });
          return;
        }

        const { data: identical } = await findPickupCandidatesForUnit(accessControl, unit, endOfToday);
        const seen = new Set<string>();
        const candidates = [...allocatedToday, ...identical].filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));

        if (candidates.length === 0) {
          feedback('error');
          setNotFound({
            code,
            message: unit.active
              ? t('scan.notFoundItem', { item: label })
              : `${t('scan.notFoundItem', { item: label })} ${t('scan.inactiveItem', { item: unitLabel(unit) })}`,
          });
          return;
        }
        feedback('ok');
        setPicker({ itemLabel: label, bookings: candidates, unit });
        return;
      }

      const product = res.product;
      const { data: bookings } = await findBookingsForProduct(accessControl, product.id, endOfToday);
      if (bookings.length === 0) {
        feedback('error');
        setNotFound({ code, message: t('scan.notFoundItem', { item: product.product_name }) });
        return;
      }
      feedback('ok');
      if (bookings.length === 1) {
        openBooking(bookings[0], { productTypeId: product.id });
        return;
      }
      setPicker({ itemLabel: product.product_name, bookings, productTypeId: product.id });
    } finally {
      setBusy(false);
    }
  };

  useScanHandler(handleScan, true, { fallback: true });

  const now = Date.now();

  return (
    <>
      {busy && (
        <div
          style={{ bottom: 16, right: 16 }}
          className="fixed z-50 flex items-center gap-sm px-md py-sm rounded-lg shadow-lg bg-primary border border-primary text-sm text-secondary">
          <Loader2 className="w-4 h-4 animate-spin text-blue" />
          {t('scan.lookingUp')}
        </div>
      )}

      {picker && (
        <ScanModal
          title={t('scan.pickerTitle')}
          subtitle={picker.itemLabel}
          onClose={() => setPicker(null)}
          testId="scan-booking-picker"
        >
          <p className="text-sm text-secondary mb-md">{t('scan.pickerHint')}</p>
          <div className="space-y-sm">
            {picker.bookings.map((b) => {
              const late = b.booking_state === 'open' && new Date(b.start_date).getTime() < now;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => openBooking(b, { unit: picker.unit, productTypeId: picker.productTypeId })}
                  className="w-full p-md rounded-lg border border-primary hover:border-blue hover:bg-blue/5 text-left flex items-center gap-md"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-sm">
                      <span className="font-semibold text-primary">{b.order_id}</span>
                      <span className="text-primary truncate">{`${b.customer_firstname || ''} ${b.customer_lastname || ''}`.trim()}</span>
                      <span className={`badge ${b.booking_state === 'active' ? 'badge-success' : 'badge-info'}`}>
                        {b.booking_state === 'active' ? t('scan.return') : t('scan.pickup')}
                      </span>
                      {late && <span className="badge badge-danger">{t('scan.late')}</span>}
                    </div>
                    <div className="text-xs text-secondary mt-xs">
                      {formatDateTime(b.start_date)} · {b.matchingLines.map((l) => `${l.item_name}${l.size ? ` (${l.size})` : ''}`).join(', ')}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-secondary flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </ScanModal>
      )}

      {notFound && (
        <ScanModal
          title={t('scan.notFoundTitle')}
          onClose={() => setNotFound(null)}
          testId="scan-not-found"
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setNotFound(null)}>
                {t('scan.close')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const code = notFound.code;
                  setNotFound(null);
                  navigate('/bookings/quick', { state: { scanCode: code, at: Date.now() } });
                }}
              >
                {t('scan.startQuickBooking')}
              </button>
            </>
          }
        >
          <p className="text-sm text-primary">{notFound.message}</p>
        </ScanModal>
      )}

      {returnStart && (
        <ReturnScanModal
          start={returnStart}
          onClose={() => setReturnStart(null)}
          onSwitch={(next) => setReturnStart(next)}
        />
      )}
    </>
  );
}
